/**
 * Loader czcionek Google Fonts dla Fabric.js.
 *
 * Czcionka trafia do cache dopiero po udanym zaladowaniu co najmniej jednego
 * wariantu. Nieudana pierwsza proba nie blokuje kolejnych renderowan.
 *
 * Ladujemy tez kursywe - bez niej pierwszy rysunek po odswiezeniu strony (Ctrl+F5) uzywal
 * zastepczej czcionki, bo kursywa z arkusza w index.html doczytuje sie dopiero przy pierwszym uzyciu.
 */

import { cache } from 'fabric';

const loadedFonts = new Set();
const loadingFonts = new Map();

async function loadFont(font) {
    if (loadedFonts.has(font)) return;
    if (loadingFonts.has(font)) return loadingFonts.get(font);

    const promise = loadFontWithRetry(font)
        .then(() => {
            loadedFonts.add(font);
        })
        .finally(() => {
            loadingFonts.delete(font);
        });

    loadingFonts.set(font, promise);
    return promise;
}

async function loadFontWithRetry(font, attempts = 2) {
    let lastError;

    for (let attempt = 1; attempt <= attempts; attempt += 1) {
        try {
            await loadFontVariant(font);
            return;
        } catch (error) {
            lastError = error;
            if (attempt < attempts) {
                await new Promise(resolve => setTimeout(resolve, 300));
            }
        }
    }

    throw lastError;
}

async function loadFontVariant(font) {
    // Google pomija warianty, ktorych kroj nie ma (np. DM Serif Display nie ma pogrubienia)
    const cssUrl = `https://fonts.googleapis.com/css2?family=${font.replace(/ /g, '+')}:ital,wght@0,400;0,700;1,400;1,700&display=swap`;

    const response = await fetch(cssUrl);
    if (!response.ok) {
        throw new Error(`Google Fonts CSS request failed with status ${response.status}`);
    }

    const cssText = await response.text();
    const faces = parseFontFaces(cssText);

    if (faces.length === 0) {
        throw new Error(`No font faces found for ${font}`);
    }

    const results = await Promise.allSettled(
        faces.map(async ({ family, weight, style, src, unicodeRange }) => {
            const descriptors = { weight, style };
            if (unicodeRange) {
                descriptors.unicodeRange = unicodeRange;
            }

            const face = new FontFace(family, src, descriptors);
            await face.load();
            document.fonts.add(face);
        })
    );

    const loadedCount = results.filter(result => result.status === 'fulfilled').length;
    if (loadedCount === 0) {
        throw new Error(`No variants loaded for ${font}`);
    }

    if (document.fonts?.ready) {
        await document.fonts.ready;
    }
}

function parseFontFaces(cssText) {
    const fontFaceRegex = /@font-face\s*\{([^}]+)\}/g;
    const faces = [];
    let match;

    while ((match = fontFaceRegex.exec(cssText)) !== null) {
        const block = match[1];
        const family = block.match(/font-family:\s*'([^']+)'/)?.[1];
        const weight = block.match(/font-weight:\s*(\d+)/)?.[1] || '400';
        const style = block.match(/font-style:\s*(\w+)/)?.[1] || 'normal';
        const src = block.match(/src:\s*(url\([^)]+\)[^;]*)/)?.[1];
        const unicodeRange = block.match(/unicode-range:\s*([^;]+)/)?.[1]?.trim();

        if (family && src) {
            faces.push({ family, weight, style, src, unicodeRange });
        }
    }

    return faces;
}

/**
 * Zaladuj czcionki Google Fonts przez FontFace API.
 * @param {string[]} fonts - tablica nazw czcionek (np. ["Bitter", "DM Serif Display"])
 * @returns {Promise<void>}
 */
export async function loadFonts(fonts) {
    if (!fonts || fonts.length === 0) return;

    const fontsToLoad = fonts.filter(font => font && !loadedFonts.has(font));
    if (fontsToLoad.length === 0) return;

    const results = await Promise.allSettled(fontsToLoad.map(loadFont));
    results.forEach((result, index) => {
        if (result.status === 'rejected') {
            console.warn(`Nie udalo sie zaladowac czcionki: ${fontsToLoad[index]}`, result.reason);
        }
    });

    // Fabric zapamietuje szerokosci liter - pomiary zrobione zastepcza czcionka trzeba wyrzucic
    if (results.some(result => result.status === 'fulfilled')) {
        cache.clearFontCache();
    }
}

/**
 * Wszystkie czcionki potrzebne do narysowania materialu: z motywu, z elementow layoutu
 * oraz wybrane w edytorze ukladu (wzor zakladu i poprawki zmarlego).
 */
export function collectLayoutFonts(layout, ...changesList) {
    const fonts = new Set(layout?.theme?.fonts || []);
    (layout?.elements || []).forEach((element) => {
        if (element.style?.fontFamily) fonts.add(element.style.fontFamily);
    });
    changesList.forEach((changes) => {
        Object.values(changes?.elements || {}).forEach((props) => {
            if (props?.fontFamily) fonts.add(props.fontFamily);
        });
        (changes?.added || []).forEach((definition) => {
            if (definition?.fontFamily) fonts.add(definition.fontFamily);
        });
    });
    return [...fonts].filter(font => typeof font === 'string' && font.trim() && !font.includes('{'));
}

/**
 * Sprawdz czy czcionka jest zaladowana.
 * @param {string} font - nazwa czcionki
 * @returns {boolean}
 */
export function isFontLoaded(font) {
    return loadedFonts.has(font);
}
